
import licenseApi from "../api/license/licenseApi";
import { defineComponent, onMounted, ref, watch, computed } from "vue";
import CrmNavbar from "./Navbar.vue";
import { useOfficeState } from "../store/officeState";
import { licenseLevel } from "../model/LicenseLevel";
import { debounce } from "lodash";
import projectsApi from "../api/project/projectsApi";
import ProjectPreview from "../model/ProjectPreview";
import CrmProjectPreview from "../components/Project/ProjectPreview.vue";
import { useEmailService } from "../services/emailService";
import emailMessagesApi from "../api/emailMessage/emailMessagesApi";

/* global Office */

export default defineComponent({
  name: "CrmProjectEmail",
  components: { CrmNavbar, CrmProjectPreview },
  setup() {
    const searchText = ref("" as string);
    const isLoading = ref(false as boolean);
    const { mailboxItem } = useOfficeState();
    const isLicenseLevelValid = ref(true);
    const projectList = ref([] as ProjectPreview[]);
    const { associateEmailToProject } = useEmailService();
    const associatedProjects = ref([] as ProjectPreview[]);

    const associatedProjectsIds = computed(() => {
      return associatedProjects.value.map((ap) => ap.id);
    });

    // Cannot use directly mailboxItem because it is a computedRef (not compatible with Office js lib)
    let currentMailBoxItem = Office.context.mailbox.item;
    let from = ref(currentMailBoxItem?.from as Office.EmailAddressDetails);

    /**
     * Watch Outlook mailbox item changed
     */
    watch(mailboxItem, async () => {
      currentMailBoxItem = Office.context.mailbox.item;
      from.value = currentMailBoxItem?.from as Office.EmailAddressDetails;
      debounceSearch();
      await setAssociatedProjects();
    });

    /**
     * When component is mounted
     */
    onMounted(async () => {
      await initLicenceLevelValidity();
      await setAssociatedProjects();
    });

    /**
     * Initialize licence level validity
     */
    async function initLicenceLevelValidity() {
      let result = await licenseApi.getMobiCrmLicense();
      if (result.isSuccess) {
        isLicenseLevelValid.value = result.data.level >= licenseLevel.ENTREPRISE;
      }
    }

    /**
     * Navigates to project url
     * @param projectUrl
     */
    function navigateToProjectUrl(projectUrl: string) {
      if (!projectUrl) {
        return;
      }
      if (Office.context.ui.openBrowserWindow) {
        Office.context.ui.openBrowserWindow(projectUrl);
      } else {
        window.open(projectUrl);
      }
    }

    /**
     * Runs the search on the server
     **/
    const debounceSearch = debounce(async function () {
      isLoading.value = true;
      if (searchText.value) {
        const result = await projectsApi.search(searchText.value);
        if (result.isSuccess) {
          projectList.value = result.data.items.filter(
            (p: ProjectPreview) => !associatedProjectsIds.value.includes(p.id)
          );
        }
      } else {
        projectList.value = [];
      }
      isLoading.value = false;
    }, 500);

    /**
     * Associates current email to selected project
     * @param projectId
     */
    async function associateCurrentEmailToProject(projectId: string) {
      isLoading.value = true;
      await associateEmailToProject(projectId, Office.context.mailbox.item, associateCurrentEmailToProjectCallback);
    }

    /**
     * Associates current email to selected contact callback
     */
    async function associateCurrentEmailToProjectCallback() {
      await setAssociatedProjects();
      isLoading.value = false;
    }

    /**
     * Sets associated projects
     */
    async function setAssociatedProjects() {
      isLoading.value = true;
      if (Office.context.mailbox.item) {
        let result = await emailMessagesApi.getAssociatedProjects(Office.context.mailbox.item.itemId);
        if (result.isSuccess) {
          associatedProjects.value = result.data;
        } else {
          associatedProjects.value = [];
        }
      }
      isLoading.value = false;
    }

    return {
      from,
      searchText,
      navigateToProjectUrl,
      isLoading,
      isLicenseLevelValid,
      debounceSearch,
      projectList,
      associatedProjects,
      associateCurrentEmailToProject,
      associatedProjectsIds,
    };
  },
});
